// Web audio picker for a custom per-conversation notification sound. Opens the
// native file dialog, reads the chosen audio as a data URL (capped so it stays
// small enough to persist locally), and returns its display name + data URL.
// Returns null if the user cancels or the file is too large / unreadable.

const MAX_BYTES = 512 * 1024; // 512 KB — a short notification tone, not music.

export interface PickedAudio {
  readonly name: string;
  readonly dataUrl: string;
}

export async function pickAudioFile(): Promise<PickedAudio | null> {
  if (typeof document === 'undefined') return null;

  const file = await new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*';
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
  if (file === null) return null;
  if (file.size > MAX_BYTES) return null;

  const dataUrl = await new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
  if (dataUrl === null) return null;

  return { name: file.name.replace(/\.[^.]+$/, '') || 'Custom', dataUrl };
}
